import React from 'react';
import {
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CalendarIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import type { ConferenciasFilters } from '../../hooks/useConferencias';

interface ConferenciasToolbarProps {
  filters: ConferenciasFilters;
  setFilters: (filters: ConferenciasFilters) => void;
  usuarios: string[];
  onRefresh: () => void;
  isLoading?: boolean;
}

const ConferenciasToolbar: React.FC<ConferenciasToolbarProps> = ({
  filters,
  setFilters,
  usuarios,
  onRefresh,
  isLoading = false,
}) => {
  const [showAdvancedFilters, setShowAdvancedFilters] = React.useState(false);

  const hasActiveFilters = Object.keys(filters).some(key => 
    key !== 'page' && key !== 'pageSize' && filters[key as keyof ConferenciasFilters]
  );

  const clearFilters = () => {
    setFilters({ page: 1, pageSize: filters.pageSize || 10 });
    setShowAdvancedFilters(false);
  };

  const updateFilter = (key: keyof ConferenciasFilters, value: string | number | undefined) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-zinc-200 dark:border-zinc-700 dark:bg-zinc-800">
      {/* Barra principal */}
      <div className="p-4 md:p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Busca */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-zinc-400" />
            <input
              type="text"
              placeholder="Buscar por NFe, fornecedor, código ou usuário..."
              value={filters.search || ''}
              onChange={(e) => updateFilter('search', e.target.value || undefined)}
              className="w-full pl-10 pr-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white dark:placeholder-zinc-400"
            />
          </div>

          {/* Filtro de Status */}
          <div className="sm:w-48">
            <select
              value={filters.status || 'TODOS'}
              onChange={(e) => updateFilter('status', e.target.value === 'TODOS' ? undefined : e.target.value)}
              className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white dark:border-zinc-600 dark:bg-zinc-700 dark:text-white"
            >
              <option value="TODOS">Todos os Status</option>
              <option value="EM_ANDAMENTO">Em Andamento</option>
              <option value="CONCLUIDO">Concluído</option>
              <option value="CANCELADO">Cancelado</option>
            </select>
          </div>

          {/* Botões de ação */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`p-3 rounded-lg transition-colors ${
                showAdvancedFilters || hasActiveFilters
                  ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                  : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-600 dark:bg-zinc-700 dark:hover:bg-zinc-600 dark:text-zinc-400'
              }`}
              title="Filtros avançados"
            >
              <FunnelIcon className="h-5 w-5" />
            </button>

            <button
              onClick={onRefresh}
              disabled={isLoading}
              className="p-3 bg-zinc-100 hover:bg-zinc-200 rounded-lg transition-colors disabled:opacity-50 text-zinc-600 dark:bg-zinc-700 dark:hover:bg-zinc-600 dark:text-zinc-400"
              title="Atualizar dados"
            >
              <svg
                className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 text-sm text-zinc-600 hover:text-zinc-800 transition-colors dark:text-zinc-400 dark:hover:text-zinc-200"
              >
                Limpar filtros
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filtros avançados */}
      {showAdvancedFilters && (
        <div className="border-t border-zinc-200 dark:border-zinc-700 p-4 md:p-6 bg-zinc-50 dark:bg-zinc-800/50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Data Início */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                <CalendarIcon className="inline h-4 w-4 mr-1" />
                Data Início
              </label>
              <input
                type="date"
                value={filters.dataInicio || ''}
                onChange={(e) => updateFilter('dataInicio', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white"
              />
            </div>

            {/* Data Fim */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                <CalendarIcon className="inline h-4 w-4 mr-1" />
                Data Fim
              </label>
              <input
                type="date"
                value={filters.dataFim || ''}
                onChange={(e) => updateFilter('dataFim', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white"
              />
            </div>

            {/* Usuário */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                <UserIcon className="inline h-4 w-4 mr-1" />
                Usuário
              </label>
              <select
                value={filters.usuario || ''}
                onChange={(e) => updateFilter('usuario', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white dark:border-zinc-600 dark:bg-zinc-700 dark:text-white"
              >
                <option value="">Todos os usuários</option>
                {usuarios.map((usuario) => (
                  <option key={usuario} value={usuario}>
                    {usuario}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-4 flex items-center justify-between">
              <div className="flex flex-wrap gap-2">
                {filters.search && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    Busca: {filters.search}
                    <button
                      onClick={() => updateFilter('search', undefined)}
                      className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {filters.status && filters.status !== 'TODOS' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                    Status: {filters.status}
                    <button
                      onClick={() => updateFilter('status', undefined)}
                      className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-emerald-200 dark:hover:bg-emerald-800"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {filters.usuario && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                    Usuário: {filters.usuario}
                    <button
                      onClick={() => updateFilter('usuario', undefined)}
                      className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-purple-200 dark:hover:bg-purple-800"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                )}
              </div>
              
              <button
                onClick={clearFilters}
                className="text-sm text-zinc-600 hover:text-zinc-800 font-medium dark:text-zinc-400 dark:hover:text-zinc-200"
              >
                Limpar todos
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConferenciasToolbar;